import React, { useState } from 'react';
import {
	Box,
	Card,
	CardContent,
	Typography,
	Button,
	Divider,
	IconButton,
	useMediaQuery,
	useTheme,
	Grid2,
} from '@mui/material';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { PictureAsPdfOutlined } from '@mui/icons-material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import KYcForm from '../Forms/KYcdetails';
import { MePUTKYC } from '../../../../../services/api/Vendorpage/me';
import ProfileModalResponse from '../../Vendor-ui/profilemodal';

interface KYCDetails {
	gstNumber: string;
	companyDocument: string;
	gstDocument: string;
	accountHolder: string;
	accountNumber: string;
	accountType: string;
	branch: string;
	bankName: string;
	ifscCode: string;
}

const initialKYC: KYCDetails = {
	gstNumber: '27ABCDE1234FF1Z5',
	companyDocument: 'CompanyRegistrationDocument.pdf',
	gstDocument: 'GSTDocument.pdf',
	accountHolder: 'Sarah Johnson',
	accountNumber: '1234567890',
	accountType: 'Savings',
	branch: 'Chennai Main Branch',
	bankName: 'HDFC Bank',
	ifscCode: 'HDFC00098625',
};

export default function KYCCard() {
	const [kyc, setKYC] = useState<KYCDetails>(initialKYC);
	const [open, setOpen] = useState(false);
	const slug = localStorage.getItem('slug');
	const [responseModal, setResponseModal] = useState<any>({
		open: false,
		isSuccess: false,
		message: '',
		responseData: '',
	});

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	const handleChange = (field: keyof KYCDetails, value: string) => {
		setKYC((prev) => ({ ...prev, [field]: value }));
	};

	const handleSave = async (formData: any) => {
		const response = await MePUTKYC(`vendors/${slug}/me/kyc`, formData);
		if (response.success) {
			setResponseModal({
				open: true,
				isSuccess: true,
				message: 'Profile updated successfully!',
				responseData: null,
			});
		} else {
			setResponseModal({
				open: true,
				isSuccess: false,
				message: 'Profile update failed!',
				responseData: null,
			});
		}
		handleClose();
	};

	return (
		<Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
			{/* KYC Card */}
			<Card
				sx={{
					borderRadius: 3,
					boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
					border: '1px solid #e0e0e0',
				}}
			>
				<Box
					sx={{
						backgroundColor: '#1B663C',
						p: { xs: 1, sm: 2 },
						color: 'white',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						flexWrap: 'wrap',
						gap: 1,
					}}
				>
					<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
						<EditNoteRoundedIcon />
						<Typography sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }} fontWeight={600}>
							KYC Details
						</Typography>
					</Box>
					<IconButton onClick={handleOpen} sx={{ color: 'white' }}>
						<DriveFileRenameOutlineIcon />
					</IconButton>
				</Box>

				<CardContent>
					<Grid2 container spacing={isMobile ? 2 : 3}>
						{/* Company Details Section */}
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Box>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
									<BusinessOutlinedIcon color="primary" />
									<Typography
										fontWeight="600"
										color="primary"
										sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }}
										fontFamily="Poppins"
									>
										COMPANY DETAILS
									</Typography>
								</Box>
								<Divider sx={{ mb: 2 }} />

								<Box mb={3}>
									<Typography variant="body2" color="primary">
										GST Number
									</Typography>
									<Typography
										sx={{ fontSize: { xs: 13, sm: 15, md: 16 } }}
										fontWeight="medium"
									>
										{kyc.gstNumber}
									</Typography>
								</Box>

								<Box mb={2}>
									<Typography variant="body2" color="primary" mb={1}>
										Documents
									</Typography>
									<Box display="flex" flexDirection="column" gap={1}>
										<Button
											variant="outlined"
											startIcon={<PictureAsPdfOutlined />}
											fullWidth
											sx={{
												fontSize: { xs: 12, sm: 14 },
												justifyContent: 'flex-start',
												textTransform: 'none',
												color: '#424242',
												borderColor: '#e0e0e0',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											}}
										>
											{kyc.companyDocument}
										</Button>
										<Button
											variant="outlined"
											startIcon={<PictureAsPdfOutlined />}
											fullWidth
											sx={{
												fontSize: { xs: 12, sm: 14 },
												justifyContent: 'flex-start',
												textTransform: 'none',
												color: '#424242',
												borderColor: '#e0e0e0',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											}}
										>
											{kyc.gstDocument}
										</Button>
									</Box>
								</Box>
							</Box>
						</Grid2>

						{/* Bank Details Section */}
						<Grid2 size={{ xs: 12, sm: 6 }}>
							<Box>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
									<AccountBalanceOutlinedIcon color="primary" />
									<Typography
										fontWeight="600"
										color="primary"
										sx={{ fontSize: { xs: 14, sm: 16, md: 18 } }}
										fontFamily="Poppins"
									>
										BANK DETAILS
									</Typography>
								</Box>
								<Divider sx={{ mb: 2 }} />

								<Grid2 container spacing={2}>
									{[
										{ label: 'Account Holder', value: kyc.accountHolder },
										{ label: 'Account Number', value: kyc.accountNumber },
										{ label: 'Account Type', value: kyc.accountType },
										{ label: 'Bank Name', value: kyc.bankName },
										{ label: 'Branch', value: kyc.branch },
										{ label: 'IFSC Code', value: kyc.ifscCode },
									].map((item, index) => (
										<Grid2 size={{ xs: 12, sm: 6 }} key={index}>
											<Typography
												sx={{ fontSize: { xs: 12, sm: 14 } }}
												color="primary"
											>
												{item.label}
											</Typography>
											<Typography
												sx={{ fontSize: { xs: 13, sm: 15, md: 16 } }}
												fontWeight="medium"
											>
												{item.value}
											</Typography>
										</Grid2>
									))}
								</Grid2>
							</Box>
						</Grid2>
					</Grid2>
				</CardContent>
			</Card>

			{/* Edit Dialog */}
			<KYcForm
				open={open}
				handleClose={handleClose}
				handleChange={handleChange}
				kyc={kyc}
				handleSave={handleSave}
			/>
			<ProfileModalResponse
				open={responseModal.open}
				onClose={() => setResponseModal((prev: any) => ({ ...prev, open: false }))}
				isSuccess={responseModal.isSuccess}
				successMessage={responseModal.message}
				errorMessage={responseModal.message}
				responseData={responseModal.responseData}
			/>
		</Box>
	);
}
